import { useMemo, useState } from 'react';
import { Button, Flex, Stack, Text } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { ScheduledEmailStatus, type ScheduledEmailRes } from '@dealstack/shared/lib/backendClient';
import { DocumentWorkflowInstanceStatus } from '@dealstack/shared/lib/generated';
import {
	formatStringDateToRelative,
	formatStringDateToUI,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { useScheduledEmails } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useScheduledEmails.tsx';
import { useScheduledEmailActions } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useScheduledEmailActions.tsx';
import { useCreateScheduledEmail } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useCreateScheduledEmail.tsx';
import { useSetScheduledEmailEndDate } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useSetScheduledEmailEndDate.tsx';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	BADGE_VARIANT_COLOR,
	getLastPendingCount,
	getScheduledQueueBadgeVariant,
	isCapReached,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/AutomatedEmails/getScheduledQueueColumns';
import { RescheduleModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/AutomatedEmails/RescheduleModal';
import { ScheduledQueueConfirmModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/AutomatedEmails/ScheduledQueueConfirmModal';

interface Props {
	dealId: string;
	mipId: string;
	documentWorkflowInstanceId: string;
	workflowStatus: DocumentWorkflowInstanceStatus;
}

export const EmailScheduleTab = ({
	dealId,
	mipId: programmeId,
	documentWorkflowInstanceId,
	workflowStatus,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const [confirmAction, setConfirmAction] = useState<'send-now' | 'cancel' | 'restart' | null>(
		null
	);
	const [rescheduleOpen, setRescheduleOpen] = useState(false);

	const { data: schedules, isLoading } = useScheduledEmails({
		dealId,
		programmeId,
		documentWorkflowInstanceId,
	});
	const schedule = useMemo<ScheduledEmailRes | undefined>(() => schedules?.[0], [schedules]);

	const {
		cancel,
		isCancelPending,
		reschedule,
		isReschedulePending,
		restart,
		isRestartPending,
		sendNow,
		isSendNowPending,
	} = useScheduledEmailActions({
		dealId,
		programmeId,
		onSettled: () => {
			setConfirmAction(null);
			setRescheduleOpen(false);
		},
	});
	const { startReminders, isPending: isStarting } = useCreateScheduledEmail({
		dealId,
		programmeId,
	});
	const { setEndDate } = useSetScheduledEmailEndDate({ dealId, programmeId });

	if (isLoading) {
		return null;
	}

	const isDocumentCompleted = workflowStatus === DocumentWorkflowInstanceStatus.COMPLETED;

	if (!schedule) {
		return (
			<Stack gap="sm" mt="0.5rem" px="1rem" pb="1rem" data-testid="email-schedule-empty">
				{isDocumentCompleted ? (
					<Text size="sm" c="dark.0">
						{tMipManage('automated_emails.workflow_schedule.disabled_completed')}
					</Text>
				) : (
					<>
						<Text size="sm" c="dark.0">
							{tMipManage('automated_emails.workflow_schedule.empty.message')}
						</Text>
						<Text size="xs" c="dark.0">
							{tMipManage('automated_emails.workflow_schedule.empty.hint')}
						</Text>
						<Button
							data-testid="email-schedule-start"
							loading={isStarting}
							onClick={() => startReminders(documentWorkflowInstanceId)}
						>
							{tMipManage('automated_emails.workflow_schedule.start')}
						</Button>
					</>
				)}
			</Stack>
		);
	}

	const variant = getScheduledQueueBadgeVariant(schedule);
	// Gates the controls but not the badge: a row can still read ACTIVE in the moment
	// between the document completing and the listener closing it.
	const showLiveControls =
		schedule.status === ScheduledEmailStatus.ACTIVE && !isDocumentCompleted;
	// Restart only for the cap-reached bucket (still-pending signers after maxFires),
	// matching the queue's row-actions. Plain ALL_RESOLVED completions are NOT
	// restartable — everyone signed, so a restart would immediately re-complete.
	const showRestart = isCapReached(schedule) && !isDocumentCompleted;
	const confirmCopy =
		confirmAction &&
		{
			'send-now': {
				title: tMipManage('automated_emails.scheduled_queue.confirm_modal.send_now.title'),
				description: tMipManage(
					'automated_emails.scheduled_queue.confirm_modal.send_now.description'
				),
				confirmLabel: tMipManage('automated_emails.scheduled_queue.actions.send_now'),
				pending: isSendNowPending,
				run: () => sendNow(schedule.id),
			},
			cancel: {
				title: tMipManage('automated_emails.scheduled_queue.confirm_modal.cancel.title'),
				description: tMipManage(
					'automated_emails.scheduled_queue.confirm_modal.cancel.description'
				),
				confirmLabel: tMipManage('automated_emails.scheduled_queue.actions.cancel'),
				pending: isCancelPending,
				run: () => cancel(schedule.id),
			},
			restart: {
				title: tMipManage('automated_emails.scheduled_queue.confirm_modal.restart.title'),
				description: tMipManage(
					'automated_emails.scheduled_queue.confirm_modal.restart.description'
				),
				confirmLabel: tMipManage('automated_emails.scheduled_queue.actions.restart'),
				pending: isRestartPending,
				run: () => restart(schedule.id),
			},
		}[confirmAction];

	return (
		<Stack gap="md" mt="0.5rem" px="1rem" pb="1rem" data-testid="email-schedule-card">
			<Flex align="center" gap="sm">
				<StatusBadge
					text={tMipManage(`automated_emails.scheduled_queue.status_badge.${variant}`)}
					color={BADGE_VARIANT_COLOR[variant]}
				/>
			</Flex>

			{showLiveControls && (
				<Flex direction="column" gap={0}>
					<Text size="xs" c="dark.0">
						{tMipManage('automated_emails.scheduled_queue.columns.next_send')}
					</Text>
					<Text size="sm">
						{formatStringDateToRelative(schedule.nextFireAt) ??
							formatStringDateToUI(schedule.nextFireAt)}
					</Text>
					<Text size="xs" c="dark.0">
						{formatStringDateToUI(schedule.nextFireAt)}
					</Text>
				</Flex>
			)}

			{showLiveControls && schedule.endDate && (
				<Flex direction="column" gap={0}>
					<Text size="xs" c="dark.0">
						{tMipManage('automated_emails.workflow_schedule.end_date')}
					</Text>
					<Text size="sm">{formatStringDateToUI(schedule.endDate)}</Text>
				</Flex>
			)}

			{showLiveControls && (
				<DateInput
					label={tMipManage('automated_emails.workflow_schedule.set_end_date')}
					aria-label="End date"
					data-testid="email-schedule-end-date-input"
					value={schedule.endDate ? new Date(schedule.endDate) : null}
					onChange={(value) =>
						setEndDate(schedule.id, value ? value.toISOString() : null)
					}
					minDate={new Date()}
					clearable
					clearButtonProps={{ 'aria-label': 'Clear end date' }}
					popoverProps={{ withinPortal: true, zIndex: 1000 }}
				/>
			)}

			<Flex gap="xl">
				<Flex direction="column" gap={0}>
					<Text size="xs" c="dark.0">
						{tMipManage('automated_emails.scheduled_queue.columns.progress')}
					</Text>
					<Text size="sm">
						{schedule.fireCount}/{schedule.maxFires}
					</Text>
				</Flex>
				<Flex direction="column" gap={0}>
					<Text size="xs" c="dark.0">
						{tMipManage('automated_emails.scheduled_queue.columns.still_pending')}
					</Text>
					<Text size="sm">{getLastPendingCount(schedule.lastPendingCount)}</Text>
				</Flex>
			</Flex>

			<Flex gap="xs">
				{showLiveControls && (
					<>
						<Button
							data-testid="email-schedule-send-now"
							onClick={() => setConfirmAction('send-now')}
						>
							{tMipManage('automated_emails.scheduled_queue.actions.send_now')}
						</Button>
						<Button
							variant="default"
							data-testid="email-schedule-reschedule"
							onClick={() => setRescheduleOpen(true)}
						>
							{tMipManage('automated_emails.scheduled_queue.actions.reschedule')}
						</Button>
						<Button
							variant="default"
							color="red"
							data-testid="email-schedule-cancel"
							onClick={() => setConfirmAction('cancel')}
						>
							{tMipManage('automated_emails.scheduled_queue.actions.cancel')}
						</Button>
					</>
				)}
				{showRestart && (
					<Button
						data-testid="email-schedule-restart"
						onClick={() => setConfirmAction('restart')}
					>
						{tMipManage('automated_emails.scheduled_queue.actions.restart')}
					</Button>
				)}
			</Flex>

			{rescheduleOpen && (
				<RescheduleModal
					initialNextFireAt={schedule.nextFireAt}
					isSubmitting={isReschedulePending}
					onConfirm={(nextFireAt) => reschedule(schedule.id, nextFireAt)}
					onClose={() => setRescheduleOpen(false)}
				/>
			)}
			{confirmCopy && (
				<ScheduledQueueConfirmModal
					title={confirmCopy.title}
					description={confirmCopy.description}
					confirmLabel={confirmCopy.confirmLabel}
					isSubmitting={confirmCopy.pending}
					onConfirm={confirmCopy.run}
					onClose={() => setConfirmAction(null)}
				/>
			)}
		</Stack>
	);
};
